"use client";

import { toast } from "sonner";
import { incrementPromptCopyCount } from "@/lib/actions";

/**
 * A custom hook for copying prompts to clipboard with rate-limiting
 * to prevent spamming the count increment
 * 
 * @param promptId The ID of the prompt being copied
 * @returns A function to handle copying with rate limited tracking
 */
export function usePromptCopy(promptId: string) {
  // Function that will be returned from the hook
  const copyToClipboard = async (text: string) => {
    // First, copy the text to clipboard
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Prompt copied to clipboard!");
      
      // After copying, check if we should increment the count
      const shouldIncrementCount = checkRateLimit(promptId);
      
      // Only call the server action if rate limiting allows
      if (shouldIncrementCount) {
        incrementPromptCopyCount(promptId);
      }
    } catch (error) {
      console.error("Failed to copy text:", error);
      toast.error("Failed to copy to clipboard");
    }
  };
  
  return copyToClipboard;
}

/**
 * Checks if the prompt copy count should be incremented based on rate limiting
 * Currently limits to one count increment per prompt per 30 minutes
 */
function checkRateLimit(promptId: string): boolean {
  // Skip rate limiting if not in browser environment
  if (typeof window === "undefined") return false;
  
  const storageKey = `prompt_copied_${promptId}`;
  const lastCopiedTimestamp = localStorage.getItem(storageKey);
  const now = Date.now();
  
  // If never copied before, or copied more than 30 minutes ago
  if (!lastCopiedTimestamp || (now - parseInt(lastCopiedTimestamp)) > 30 * 60 * 1000) {
    // Update the timestamp and allow increment
    localStorage.setItem(storageKey, now.toString());
    return true;
  }
  
  // Within rate limit window, don't increment
  return false;
} 